import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Download, Filter, ShieldAlert } from "lucide-react";
import { api } from "../api";
import { useAdminAuth } from "../AuthContext";

const DELIVERY = { "": "Alle", sent: "Leveret", retrying: "Prøver igen", permanently_failed: "Fejlet", pending: "Afventer" };
const INTERNAL = { "": "Alle", new: "Ny", in_progress: "I gang", waiting: "Afventer", closed: "Afsluttet" };
const OMR = {
  "": "Alle", "boern-unge-familier": "Børn/unge/familier",
  "voksne-social-stabilisering": "Voksne", "beskaeftigelse-fastholdelse": "Beskæftigelse",
  "loesladelse-resocialisering": "Løsladelse", andet: "Andet",
};
const INTERNAL_BADGE = {
  new: "bg-[#2D5A3F]/10 text-[#2D5A3F]", in_progress: "bg-amber-100 text-amber-700",
  waiting: "bg-blue-100 text-blue-700", closed: "bg-neutral-200 text-neutral-600",
};

const fmtDate = (s) => (s ? new Date(s).toLocaleString("da-DK", { dateStyle: "medium", timeStyle: "short" }) : "");

const Inbox = () => {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ delivery_status: "", internal_status: "", indsatsomraade: "" });

  const load = useCallback(() => {
    setRes(null);
    api.messages({ page, page_size: 20, ...filters })
      .then(setRes)
      .catch(() => setError("Kunne ikke hente indbakke."));
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };
  const totalPages = res ? Math.max(1, Math.ceil(res.total / res.page_size)) : 1;

  return (
    <div data-testid="admin-inbox-page">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-[#1A2E24]">Indbakke</h1>
          <p className="text-sm text-[#5C7567] mt-1 flex items-center gap-1.5">
            <ShieldAlert className="h-4 w-4" /> Oplysninger er maskeret. Åbn en henvendelse for at se fulde data (logges).
          </p>
        </div>
        {user?.role === "admin" && (
          <a
            href={api.exportUrl()}
            data-testid="admin-export-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#2D5A3F] text-white px-4 py-2 text-sm hover:bg-[#244b34] transition-colors duration-200"
          >
            <Download className="h-4 w-4" /> Eksportér (anonymiseret)
          </a>
        )}
      </header>

      <div className="rounded-2xl bg-white border border-black/5 p-4 mb-4 flex flex-wrap gap-3 items-center" data-testid="admin-inbox-filters">
        <span className="inline-flex items-center gap-1.5 text-sm text-[#5C7567]"><Filter className="h-4 w-4" /> Filtre:</span>
        {[
          ["delivery_status", DELIVERY], ["internal_status", INTERNAL], ["indsatsomraade", OMR],
        ].map(([key, opts]) => (
          <select
            key={key}
            value={filters[key]}
            onChange={(e) => setF(key, e.target.value)}
            data-testid={`admin-filter-${key}`}
            className="rounded-lg border border-black/10 px-3 py-1.5 text-sm bg-white"
          >
            {Object.entries(opts).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        ))}
      </div>

      {error && <p className="text-red-600" data-testid="admin-inbox-error">{error}</p>}
      {!res && !error && (
        <div className="flex items-center gap-2 text-[#5C7567]"><Loader2 className="h-5 w-5 animate-spin" /> Henter…</div>
      )}

      {res && (
        <>
          <div className="rounded-2xl bg-white border border-black/5 overflow-hidden">
            <table className="w-full text-sm" data-testid="admin-inbox-table">
              <thead>
                <tr className="text-left text-[#5C7567] border-b border-black/5">
                  <th className="px-4 py-3 font-medium">Modtaget</th>
                  <th className="px-4 py-3 font-medium">Navn</th>
                  <th className="px-4 py-3 font-medium">E-mail</th>
                  <th className="px-4 py-3 font-medium">Levering</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {res.items.map((m) => (
                  <tr
                    key={m.id}
                    onClick={() => navigate(`/admin/indbakke/${m.id}`)}
                    data-testid={`admin-inbox-row-${m.id}`}
                    className="border-b border-black/5 last:border-0 hover:bg-[#F7F5F0] cursor-pointer transition-colors duration-150"
                  >
                    <td className="px-4 py-3 text-[#1A2E24] whitespace-nowrap">{fmtDate(m.created_at)}</td>
                    <td className="px-4 py-3">{m.name_masked}</td>
                    <td className="px-4 py-3 text-[#5C7567]">{m.email_masked}</td>
                    <td className="px-4 py-3">{DELIVERY[m.delivery_status] || m.delivery_status || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs ${INTERNAL_BADGE[m.internal_status] || ""}`}>
                        {INTERNAL[m.internal_status] || m.internal_status}
                      </span>
                    </td>
                  </tr>
                ))}
                {res.items.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-[#5C7567]">Ingen henvendelser matcher.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4 text-sm text-[#5C7567]">
            <span data-testid="admin-inbox-total">{res.total} henvendelser</span>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-inbox-prev"
                className="rounded-lg border border-black/10 px-3 py-1.5 disabled:opacity-40 hover:bg-white">Forrige</button>
              <span>Side {page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="admin-inbox-next"
                className="rounded-lg border border-black/10 px-3 py-1.5 disabled:opacity-40 hover:bg-white">Næste</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Inbox;
